import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClockIcon, HashIcon, TrendingUpIcon, XIcon } from 'lucide-react';
import { FilterChip } from '../components/FilterChip';
import { PageHeader } from '../components/PageHeader';
import { PersonRow } from '../components/PersonRow';
import { SearchField } from '../components/SearchField';
import { SectionTitle } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { people } from '../data/people';
import { categories, recentSearches, suggestedSearches, topics } from '../data/topics';
import { useLocale } from '../contexts/LocaleContext';
export function Search() {
  const {
    t,
    tl,
    locale
  } = useLocale();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [recents, setRecents] = useState<string[]>(recentSearches);
  const term = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (term.length === 0) {
      return {
        people: [],
        topics: [],
        sports: [],
        hashtags: []
      };
    }
    return {
      people: people.filter(person => person.id !== 'me' && (person.name.toLowerCase().includes(term) || person.username.toLowerCase().includes(term))),
      topics: topics.filter(topic => tl(topic.title).toLowerCase().includes(term)),
      sports: categories.filter(category => category !== 'all' && t(`category.${category}`).toLowerCase().includes(term)),
      hashtags: suggestedSearches.filter(tag => tag.toLowerCase().includes(term.replace('#', '')))
    };
  }, [term, locale]);
  const total = results.people.length + results.topics.length + results.sports.length + results.hashtags.length;
  const commit = (value: string) => {
    setQuery(value);
    setRecents(prev => [value, ...prev.filter(item => item !== value)].slice(0, 6));
  };
  return <div className="px-2 pb-2" data-id="element-3240" data-mp="1ffhoi0or24ql">
      <PageHeader title={t('search.title')} showBack data-id="element-3241" data-mp="3cdsmvne5wrto" />

      <SearchField value={query} onChange={setQuery} autoFocus data-id="element-3242" data-mp="3cc7sbxrul8vt" />

      {term.length === 0 ? <>
          {recents.length > 0 && <section className="mt-3" data-id="element-3243" data-mp="3lf8x9v4z8txw">
              <SectionTitle action={<button type="button" onClick={() => setRecents([])} className="text-[11px] font-medium text-gold-deep transition-colors duration-150 ease-brand hover:text-deep" data-id="element-3245" data-mp="3gfw28n3ycgk8">
                    {t('search.clearAll')}
                  </button>} data-id="element-3244" data-mp="3le7a4saosgzt">
                {t('search.recent')}
              </SectionTitle>
              <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-3246" data-mp="2xo3gndr7pxmj">
                {recents.map(item => <li key={item} className="flex items-center gap-2 border-b border-sand-line last:border-b-0" data-id="element-3247" data-mp="29xr4fqoxpeq5">
                    <button type="button" onClick={() => commit(item)} className="flex min-h-[44px] flex-1 items-center gap-2 text-left text-[13px] text-deep" data-id="element-3248" data-mp="khn284h1f06r">
                      <ClockIcon size={15} className="text-muted" aria-hidden="true" data-id="element-3249" data-mp="bfxeb8md6jdq" />
                      {item}
                    </button>
                    <button type="button" onClick={() => setRecents(prev => prev.filter(entry => entry !== item))} aria-label={t('search.remove')} className="grid h-11 w-11 place-items-center rounded-pill text-muted transition-colors duration-150 ease-brand hover:text-deep" data-id="element-3250" data-mp="147h275twolm2">
                      <XIcon size={15} data-id="element-3251" data-mp="3f0jtunet949k" />
                    </button>
                  </li>)}
              </ul>
            </section>}

          <section className="mt-3" data-id="element-3252" data-mp="32ujgp1l0rtw">
            <SectionTitle data-id="element-3253" data-mp="33x9kur2yq8r">{t('search.suggested')}</SectionTitle>
            <ul className="flex flex-wrap gap-1" data-id="element-3254" data-mp="3uffez14f9w97">
              {suggestedSearches.map(tag => <li key={tag} data-id="element-3255" data-mp="3qfy6rzxm2l0c">
                  <FilterChip label={tag} onClick={() => commit(tag)} icon={<TrendingUpIcon size={13} data-id="element-3257" data-mp="m0opqvbbsp8l" />} data-id="element-3256" data-mp="3qcsl6uo6y19d" />
                </li>)}
            </ul>
          </section>

          <section className="mt-3" data-id="element-3258" data-mp="i04trv2hnro1">
            <SectionTitle data-id="element-3259" data-mp="hwz3ici4ijqq">{t('search.sports')}</SectionTitle>
            <ul className="flex flex-wrap gap-1" data-id="element-3260" data-mp="cyxlzw0cishd">
              {categories.filter(category => category !== 'all').map(category => <li key={category} data-id="element-3261" data-mp="2xpxzjufn0vub">
                    <FilterChip label={t(`category.${category}`)} onClick={() => commit(t(`category.${category}`))} data-id="element-3262" data-mp="2xr0vigrthoh3" />
                  </li>)}
            </ul>
          </section>
        </> : total === 0 ? <div className="mt-3" data-id="element-3263" data-mp="2nn2ix3zf3lup">
          <EmptyState title={t('search.empty.title')} body={t('search.empty.body', {
        term: query
      })} data-id="element-3264" data-mp="2nk6lrhve917b" />
        </div> : <>
          {results.people.length > 0 && <section className="mt-3" data-id="element-3265" data-mp="31dpg2rpj53oo">
              <SectionTitle data-id="element-3266" data-mp="31cnv9vipp2tr">{t('search.people')}</SectionTitle>
              <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-3267" data-mp="36csehtb08bno">
                {results.people.map(person => <PersonRow key={person.id} person={person} data-id="element-3268" data-mp="3bcwr388skm8m" />)}
              </ul>
            </section>}

          {results.topics.length > 0 && <section className="mt-3" data-id="element-3269" data-mp="1gydq5a3weqsp">
              <SectionTitle data-id="element-3270" data-mp="1gzf3xmcatre4">{t('search.topics')}</SectionTitle>
              <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-3271" data-mp="1lw5i244jmcww">
                {results.topics.map(topic => <li key={topic.id} className="border-b border-sand-line last:border-b-0" data-id="element-3272" data-mp="1dwrc6obaqb6d">
                    <button type="button" onClick={() => navigate('/explore')} className="w-full py-2 text-left" data-id="element-3273" data-mp="18ypjrcrzczi8">
                      <span className="block text-[13.5px] font-medium text-deep" data-id="element-3274" data-mp="1itrb8f9qadnz">
                        {tl(topic.title)}
                      </span>
                      <span className="bee-num mt-[2px] block text-[10.5px] font-bold text-gold-deep" data-id="element-3275" data-mp="1rwk24hf00nda">
                        {t('explore.topicPosts', {
                  n: topic.posts
                })}
                      </span>
                    </button>
                  </li>)}
              </ul>
            </section>}

          {results.sports.length > 0 && <section className="mt-3" data-id="element-3276" data-mp="2fjihtcvanra5">
              <SectionTitle data-id="element-3277" data-mp="2bkjc3q1qnqhn">{t('search.sports')}</SectionTitle>
              <ul className="flex flex-wrap gap-1" data-id="element-3278" data-mp="2bi6lypltu9ti">
                {results.sports.map(category => <li key={category} data-id="element-3279" data-mp="2u6h12kjzzfds">
                    <FilterChip label={t(`category.${category}`)} onClick={() => navigate('/explore')} data-id="element-3280" data-mp="2z7mw4p14reqh" />
                  </li>)}
              </ul>
            </section>}

          {results.hashtags.length > 0 && <section className="mt-3" data-id="element-3281" data-mp="2bgr9057md7pk">
              <SectionTitle data-id="element-3282" data-mp="2bfpo790sx6un">{t('search.hashtags')}</SectionTitle>
              <ul className="flex flex-wrap gap-1" data-id="element-3283" data-mp="2g9appb1lsiyl">
                {results.hashtags.map(tag => <li key={tag} data-id="element-3284" data-mp="22ifevt7jnrsd">
                    <FilterChip label={tag} onClick={() => navigate('/explore')} icon={<HashIcon size={13} data-id="element-3286" data-mp="1sieqvtyqcnhu" />} data-id="element-3285" data-mp="22faylxgt15lc" />
                  </li>)}
              </ul>
            </section>}
        </>}
    </div>;
}